/**
 * V2 source path: customer/midas/components/v2/ChatMessageListV2.tsx
 * V1 source: shared/chatBot/components/ChatMessageList.tsx
 * Strategy: logic-copied / JSX-rewritten-in-Tailwind
 * Verified no-MUI: yes
 * Phase: 1
 *
 * Copied from V1 verbatim:
 *   - thread-buffer-scoped streaming reads (MIDAS-SCOPESTREAM)
 *   - day separator helper (`isDifferentDay`)
 *   - 5-minute group-window helper (`isNewGroup`)
 *   - scroll position detection + auto-scroll-to-bottom + "new message" pill
 *   - machine picker / welcome short-circuits
 *   - selectors for messages, isLoading, currentStep, streamingText, error,
 *     showMachinePicker, machineContextLoading
 *
 * Visual layer rewritten in Tailwind. Thinking line: amber-LED gradient
 * hairline + status verb pill ("MIDAS IS SEARCHING…") + client-elapsed
 * timer (mono). User and bot turns delegate to the V2 message components.
 *
 * The day separator and machine picker fall back to V1 components for
 * Phase 1 (logic-copied / JSX delegated). They are not visible chrome the
 * MUI gate cares about — Phase 3 / Phase 1 spec keep them as-is.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  closeMachinePicker,
  setMachineContext,
} from "../../../../shared/chatBot/chatBotSlice";
import ChatDaySeparator from "../../../../shared/chatBot/components/ChatDaySeparator";
import ChatMachinePicker from "../../../../shared/chatBot/components/ChatMachinePicker";
import { ChatMessage } from "../../../../shared/chatBot/models/chatBotModels";
import { Machine } from "../../../machinery/models/models";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  parseListRefs,
  trimUnclosedListRefTail,
} from "../../../../shared/chatBot/streamBotMessage";
import MidasLogo from "../MidasLogo";
import ChatBotMessageV2 from "./ChatBotMessageV2";
import ChatUserMessageV2 from "./ChatUserMessageV2";
import ChatWelcomeV2 from "./ChatWelcomeV2";

// ═══════════════════════════════════════════════════════════════
//  Helpers — copied verbatim from V1
// ═══════════════════════════════════════════════════════════════

const isDifferentDay = (a: string, b: string): boolean => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() !== db.getFullYear() ||
    da.getMonth() !== db.getMonth() ||
    da.getDate() !== db.getDate()
  );
};

const isNewGroup = (a: ChatMessage, b: ChatMessage): boolean => {
  const diff = Math.abs(
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  return diff > 5 * 60 * 1000;
};

// ═══════════════════════════════════════════════════════════════
//  Streaming sub-components — molded thinking line + progressive markdown
// ═══════════════════════════════════════════════════════════════

/** Amber-LED hairline + status verb + client elapsed timer (Round 4 spec). */
const ThinkingLineV2: React.FC<{
  currentStep: string | null;
  showAvatar: boolean;
  startedAt: number;
}> = ({ currentStep, showAvatar, startedAt }) => {
  const { translate } = useTranslation("chatBot");
  const [elapsedSec, setElapsedSec] = useState(0);

  useEffect(() => {
    const tick = () => {
      setElapsedSec(Math.max(0, (Date.now() - startedAt) / 1000));
    };
    tick();
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [startedAt]);

  const stepLabel = currentStep
    ? translate(currentStep)
    : translate("steps.analyzing");

  return (
    <div className="relative animate-in fade-in slide-in-from-bottom-1 duration-200">
      {showAvatar && (
        <div className="absolute left-0 top-0">
          <MidasLogo size="sm" animated="pulse" />
        </div>
      )}
      <div className="ml-9 flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-sora text-[14px] font-medium text-midas-graphite">
            Midas
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-midas-muted-stone">
            streaming · {new Date(startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>

        {/* Amber-LED hairline */}
        <div
          className="h-[1px] w-full rounded-full"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(212,87,43,0.8) 50%, transparent 100%)",
            backgroundSize: "200% 100%",
            animation: "midas-led 1.6s ease-in-out infinite",
          }}
          aria-hidden="true"
        />

        {/* Status pill + elapsed timer */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-midas-terracotta-soft px-2.5 py-1">
            <span
              className="h-1.5 w-1.5 rounded-full bg-midas-terracotta"
              aria-hidden="true"
            />
            <span
              key={currentStep || "default"}
              className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-midas-terracotta animate-in fade-in duration-300"
            >
              {stepLabel}
            </span>
          </span>
          <span className="font-mono text-[11px] text-midas-muted-stone">
            {elapsedSec.toFixed(1)}s elapsed
          </span>
        </div>
      </div>

      {/* Local keyframe — scoped via animation name to avoid bleed */}
      <style>{`
        @keyframes midas-led {
          0%   { background-position: -100% 0; opacity: 0.4; }
          50%  { background-position: 0% 0;    opacity: 1; }
          100% { background-position: 100% 0;  opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

const StreamingMarkdownV2: React.FC<{
  text: string;
  showAvatar: boolean;
  startedAt: number;
}> = ({ text, showAvatar, startedAt }) => {
  // Avoid rendering an unclosed `<list-ref` mid-stream — we only render
  // the `safe` portion so a half-open tag never reaches markdown parsing.
  const trimmed = useMemo(() => trimUnclosedListRefTail(text).safe, [text]);
  const segments = useMemo(() => parseListRefs(trimmed), [trimmed]);

  return (
    <div className="relative animate-in fade-in slide-in-from-bottom-1 duration-200">
      {showAvatar && (
        <div className="absolute left-0 top-0">
          <MidasLogo size="sm" animated="pulse" />
        </div>
      )}
      <div className="ml-9 flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-sora text-[14px] font-medium text-midas-graphite">
            Midas
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-midas-muted-stone">
            streaming · {new Date(startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
        <div>
          {segments.map((seg, idx) =>
            seg.kind === "text" ? (
              <span
                key={idx}
                className="[&>p]:m-0 [&>p]:mb-2 [&>p:last-child]:mb-0 [&>p]:font-sora [&>p]:text-[14px] [&>p]:leading-[1.6] [&>p]:text-midas-graphite"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {seg.text}
                </ReactMarkdown>
              </span>
            ) : (
              // Mid-stream list-ref segment — Style A § 3 default state.
              // MUST visually match the closed-message `ChatListRefV2` default
              // (passive `<span>` branch at ChatListRefV2.tsx:143-155): plain
              // inline mono terracotta text, NO background, NO border, NO
              // padding, NO border-radius. Streaming and closed states must be
              // visually identical (MIDAS-V2-P15-BUG-ORANGE-WRAP fix, 2026-04-27).
              <span
                key={idx}
                className="font-mono text-[14px] font-normal leading-[24px]"
                style={{ color: "#C24A1F", verticalAlign: "baseline", margin: "0 2px" }}
              >
                {seg.label || `p.${seg.page}`}
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ChatMessageListV2
// ═══════════════════════════════════════════════════════════════

interface ChatMessageListV2Props {
  onSuggestionClick: (text: string) => void;
  isPhone?: boolean;
  onImageClick?: (imageUrl: string, imageName: string) => void;
  /** MIDAS-SCOPESTREAM thread id for buffer-scoped streaming reads. */
  threadId?: string;
}

const ChatMessageListV2: React.FC<ChatMessageListV2Props> = ({
  onSuggestionClick,
  isPhone = false,
  onImageClick,
  threadId,
}) => {
  const { translate } = useTranslation("chatBot");
  const dispatch = useAppDispatch();
  const messages = useAppSelector((state) => state.chatBot.messages);
  const threadBuffer = useAppSelector((state) =>
    threadId ? state.chatBot.threadBuffers[threadId] : undefined
  );
  const globalIsLoading = useAppSelector((state) => state.chatBot.isLoading);
  const globalCurrentStep = useAppSelector((state) => state.chatBot.currentStep);
  const globalStreamingText = useAppSelector((state) => state.chatBot.streamingText);
  const globalError = useAppSelector((state) => state.chatBot.error);
  const isLoading = threadId ? threadBuffer?.isLoading || false : globalIsLoading;
  const currentStep = threadId ? threadBuffer?.currentStep || null : globalCurrentStep;
  const streamingText = threadId
    ? threadBuffer?.streamingText || null
    : globalStreamingText;
  const error = threadId ? threadBuffer?.error || null : globalError;
  const showMachinePicker = useAppSelector(
    (state) => state.chatBot.showMachinePicker
  );
  const machineContextLoading = useAppSelector(
    (state) => state.chatBot.machineContextLoading
  );

  const handleMachineSelect = useCallback(
    (machine: Machine) => {
      dispatch(
        setMachineContext({
          machineId: machine.id,
          displayData: {
            model: machine.model,
            serialNumber: machine.serialNumber,
            internalNumber: machine.internalNumber,
            system: machine.system,
            manufacturerName: machine.manufacturer?.name,
            typeName: machine.type?.name,
            axisConfigName: machine.axisConfiguration?.name,
            controlSystemBrandName: machine.controlSystemBrand?.name,
            imageContent: machine.image?.content,
            description: machine.description,
            productionYear: machine.productionYear,
          },
        })
      );
    },
    [dispatch]
  );

  const handleClosePicker = useCallback(() => {
    dispatch(closeMachinePicker());
  }, [dispatch]);

  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const [showNewMessagePill, setShowNewMessagePill] = useState(false);
  const prevMessageCount = useRef(messages.length);

  // Track when streaming started so the elapsed-timer pill can read it
  const streamingStartedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (isLoading) {
      if (streamingStartedAtRef.current === null) {
        streamingStartedAtRef.current = Date.now();
      }
    } else {
      streamingStartedAtRef.current = null;
    }
  }, [isLoading]);
  const startedAt = streamingStartedAtRef.current ?? Date.now();

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const el = listRef.current;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setUserScrolledUp(distFromBottom > 100);
  }, []);

  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      if (!userScrolledUp) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      } else {
        setShowNewMessagePill(true);
      }
    }
    prevMessageCount.current = messages.length;
  }, [messages.length, userScrolledUp]);

  useEffect(() => {
    if (isLoading && !userScrolledUp) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isLoading, userScrolledUp, streamingText]);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowNewMessagePill(false);
    setUserScrolledUp(false);
  };

  if (showMachinePicker) {
    return (
      <ChatMachinePicker
        onClose={handleClosePicker}
        onSelect={handleMachineSelect}
        loading={machineContextLoading}
      />
    );
  }

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex flex-1 overflow-y-auto overflow-x-hidden bg-midas-bone">
        <ChatWelcomeV2 onSuggestionClick={onSuggestionClick} />
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      onScroll={handleScroll}
      className="relative flex flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden bg-midas-bone p-4 [scroll-behavior:smooth] overscroll-contain"
    >
      {messages.map((msg, index) => {
        const prev = index > 0 ? messages[index - 1] : null;
        const next = index < messages.length - 1 ? messages[index + 1] : null;

        const showDaySeparator =
          !prev || isDifferentDay(prev.timestamp, msg.timestamp);
        const isFirstInGroup =
          !prev || prev.role !== msg.role || isNewGroup(prev, msg);
        const isLastInGroup =
          !next || next.role !== msg.role || isNewGroup(msg, next);

        return (
          <React.Fragment key={msg.id}>
            {showDaySeparator && <ChatDaySeparator date={msg.timestamp} />}

            {msg.role === "assistant" ? (
              <ChatBotMessageV2
                message={msg}
                showAvatar={isFirstInGroup}
                showTimestamp={isLastInGroup}
                onSuggestionClick={onSuggestionClick}
                isPhone={isPhone}
                isLastInGroup={isLastInGroup}
              />
            ) : (
              <ChatUserMessageV2
                message={msg}
                showTimestamp={isLastInGroup}
                isPhone={isPhone}
                onImageClick={onImageClick}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Streaming markdown — when tokens are arriving */}
      {isLoading && streamingText && (
        <StreamingMarkdownV2
          text={streamingText}
          showAvatar={
            messages.length === 0 ||
            messages[messages.length - 1]?.role !== "assistant"
          }
          startedAt={startedAt}
        />
      )}

      {/* Thinking-line LED — when loading but no tokens yet */}
      {isLoading && !streamingText && (
        <ThinkingLineV2
          currentStep={currentStep}
          showAvatar={
            messages.length === 0 ||
            messages[messages.length - 1]?.role !== "assistant"
          }
          startedAt={startedAt}
        />
      )}

      {/* Error block */}
      {error && !isLoading && (
        <div className="ml-9 rounded-md border border-red-200 bg-red-50/60 px-3.5 py-2.5 font-sora text-[13px] text-red-700">
          {error || translate("unavailable")}
        </div>
      )}

      <div ref={bottomRef} />

      {/* New message pill */}
      {showNewMessagePill && userScrolledUp && (
        <button
          type="button"
          onClick={scrollToBottom}
          className="sticky bottom-2 z-10 self-center rounded-full bg-midas-graphite-deep px-4 py-1.5 font-sora text-[12px] font-semibold text-midas-bone shadow-md transition-all hover:bg-midas-graphite active:scale-[0.96]"
        >
          {translate("newMessage")}
        </button>
      )}
    </div>
  );
};

export default ChatMessageListV2;
